import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Video, VideoOff, Mic, MicOff, Phone, PhoneOff, Users, MessageSquare,
  Monitor, MonitorOff, Settings, MoreVertical, Maximize, Minimize,
  Copy, Check, Share2, UserPlus, Crown, X, Grid, Layout
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Participant Tile Component
const ParticipantTile = ({ participant, isLocal, isLarge }) => {
  const videoRef = useRef(null);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);

  // Attach video track when available
  useEffect(() => {
    if (participant?.videoTrack && videoRef.current) {
      participant.videoTrack.attach(videoRef.current);
    }
    return () => {
      if (participant?.videoTrack) {
        participant.videoTrack.detach();
      }
    };
  }, [participant?.videoTrack]);

  const initials = participant?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className={`relative rounded-2xl overflow-hidden bg-[#0F0F16] border border-white/10 ${
        isLarge ? 'col-span-2 row-span-2' : ''
      }`}
      data-testid={`participant-${participant?.identity || 'unknown'}`}
    >
      {videoEnabled && participant?.videoTrack ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={isLocal}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#0F0F16] to-[#08080C]">
          <div className="w-20 h-20 rounded-full bg-[#00F0FF]/20 flex items-center justify-center">
            <span className="text-2xl font-bold text-[#00F0FF]">{initials}</span>
          </div>
        </div>
      )}

      {/* Name badge */}
      <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm">
          {isLocal && <Crown className="w-3 h-3 text-[#FFE800]" />}
          <span className="text-sm font-medium text-white truncate max-w-[120px]">
            {participant?.name || 'Unknown'} {isLocal && '(You)'}
          </span>
        </div>

        <div className="flex items-center gap-1">
          {!audioEnabled && (
            <div className="p-1.5 rounded-full bg-[#FF2A2A]/80">
              <MicOff className="w-3 h-3 text-white" />
            </div>
          )}
        </div>
      </div>

      {/* Speaking indicator */}
      {participant?.isSpeaking && (
        <div className="absolute inset-0 border-2 border-[#00FF66] rounded-2xl pointer-events-none animate-pulse" />
      )}
    </motion.div>
  );
};

// Main Group Video Call Component
const GroupVideoCall = ({ roomName, onLeave }) => {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(true);
  const [error, setError] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [localParticipant, setLocalParticipant] = useState(null);
  
  // Media state
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [screenSharing, setScreenSharing] = useState(false);
  
  // UI state
  const [showChat, setShowChat] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [layout, setLayout] = useState('grid'); // grid | spotlight
  const [copied, setCopied] = useState(false);
  
  // Chat messages
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');

  // Refs
  const roomRef = useRef(null);
  const containerRef = useRef(null);

  // Connect to LiveKit room
  const connectToRoom = useCallback(async () => {
    setIsConnecting(true);
    setError(null);

    try {
      // Get LiveKit token from backend
      const response = await axios.post(`${API}/livekit/token`, {
        room_name: roomName,
        participant_name: user?.name || 'Guest',
        can_publish: true,
        can_subscribe: true,
        can_publish_data: true
      });

      const { access_token, server_url } = response.data;

      // Check if LiveKit client library is available
      if (typeof window !== 'undefined' && window.LivekitClient) {
        const { Room, RoomEvent, Track } = window.LivekitClient;

        const room = new Room({
          adaptiveStream: true,
          dynacast: true,
          videoCaptureDefaults: {
            resolution: { width: 1280, height: 720 }
          }
        });

        // Set up event listeners
        room.on(RoomEvent.Connected, () => {
          setIsConnected(true);
          setIsConnecting(false);
          setLocalParticipant({
            identity: room.localParticipant.identity,
            name: room.localParticipant.name,
            isLocal: true
          });
        });

        room.on(RoomEvent.ParticipantConnected, (participant) => {
          setParticipants(prev => [...prev, {
            identity: participant.identity,
            name: participant.name,
            isLocal: false
          }]);
        });

        room.on(RoomEvent.ParticipantDisconnected, (participant) => {
          setParticipants(prev => prev.filter(p => p.identity !== participant.identity));
        });

        room.on(RoomEvent.Disconnected, () => {
          setIsConnected(false);
        });

        room.on(RoomEvent.DataReceived, (payload, participant) => {
          const text = new TextDecoder().decode(payload);
          setMessages(prev => [...prev, {
            sender: participant.name,
            text,
            timestamp: new Date()
          }]);
        });

        // Connect to room
        await room.connect(server_url, access_token);
        
        // Enable camera and microphone
        await room.localParticipant.enableCameraAndMicrophone();

        roomRef.current = room;
      } else {
        // LiveKit client not loaded - show simulation mode
        console.warn('LiveKit client not loaded, running in simulation mode');
        
        // Simulate connection for demo purposes
        setTimeout(() => {
          setIsConnected(true);
          setIsConnecting(false);
          setLocalParticipant({
            identity: user?.id || 'local',
            name: user?.name || 'You',
            isLocal: true
          });
          
          // Add some simulated participants for demo
          setParticipants([]);
        }, 1500);
      }

    } catch (err) {
      console.error('Failed to connect to room:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to connect to video call');
      setIsConnecting(false);
    }
  }, [roomName, user]);

  useEffect(() => {
    connectToRoom();

    return () => {
      if (roomRef.current) {
        roomRef.current.disconnect();
      }
    };
  }, [connectToRoom]);

  // Toggle video
  const toggleVideo = async () => {
    if (roomRef.current) {
      await roomRef.current.localParticipant.setCameraEnabled(!videoEnabled);
    }
    setVideoEnabled(!videoEnabled);
  };

  // Toggle audio
  const toggleAudio = async () => {
    if (roomRef.current) {
      await roomRef.current.localParticipant.setMicrophoneEnabled(!audioEnabled);
    }
    setAudioEnabled(!audioEnabled);
  };

  // Toggle screen share
  const toggleScreenShare = async () => {
    try {
      if (roomRef.current) {
        await roomRef.current.localParticipant.setScreenShareEnabled(!screenSharing);
      }
      setScreenSharing(!screenSharing);
    } catch (err) {
      console.error('Screen share error:', err);
    }
  };

  // Leave call
  const leaveCall = async () => {
    if (roomRef.current) {
      roomRef.current.disconnect();
    }
    
    try {
      await axios.post(`${API}/livekit/rooms/${roomName}/leave`);
    } catch (err) {
      console.error('Error leaving room:', err);
    }
    
    onLeave?.();
  };

  // Copy room link
  const copyRoomLink = () => {
    const link = `${window.location.origin}/call/${roomName}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Send chat message
  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    if (roomRef.current) {
      const encoder = new TextEncoder();
      await roomRef.current.localParticipant.publishData(encoder.encode(newMessage), { reliable: true });
    }

    setMessages(prev => [...prev, {
      sender: user?.name || 'You',
      text: newMessage,
      timestamp: new Date(),
      isLocal: true
    }]);
    setNewMessage('');
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Render connecting state
  if (isConnecting) {
    return (
      <div className="fixed inset-0 bg-[#030305] flex items-center justify-center z-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00F0FF]/30 border-t-[#00F0FF] rounded-full animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Connecting to call...</h2>
          <p className="text-gray-400">Room: {roomName}</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="fixed inset-0 bg-[#030305] flex items-center justify-center z-50">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-[#FF2A2A]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <PhoneOff className="w-8 h-8 text-[#FF2A2A]" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Connection Failed</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={connectToRoom}
              className="px-6 py-2 rounded-full bg-[#00F0FF] text-black font-semibold hover:shadow-[0_0_20px_rgba(0,240,255,0.4)]"
            >
              Try Again
            </button>
            <button
              onClick={onLeave}
              className="px-6 py-2 rounded-full bg-[#0F0F16] text-white font-semibold border border-white/10"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const allParticipants = [localParticipant, ...participants].filter(Boolean);
  const gridCols = allParticipants.length <= 1 ? 1 : allParticipants.length <= 4 ? 2 : allParticipants.length <= 9 ? 3 : 4;

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 bg-[#030305] z-50 flex flex-col"
      data-testid="group-video-call"
    >
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3 bg-[#08080C] border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 bg-[#00FF66] rounded-full animate-pulse" />
          <span className="font-semibold text-white">{roomName}</span>
          <span className="text-gray-400 text-sm">• {allParticipants.length} participant{allParticipants.length !== 1 ? 's' : ''}</span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={copyRoomLink}
            className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0F0F16] text-sm text-gray-300 hover:text-white transition-colors"
            data-testid="copy-link-btn"
          >
            {copied ? <Check className="w-4 h-4 text-[#00FF66]" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
          
          <button
            onClick={() => setLayout(layout === 'grid' ? 'spotlight' : 'grid')}
            className="p-2 rounded-full bg-[#0F0F16] text-gray-300 hover:text-white"
            title={layout === 'grid' ? 'Switch to spotlight' : 'Switch to grid'}
          >
            {layout === 'grid' ? <Layout className="w-5 h-5" /> : <Grid className="w-5 h-5" />}
          </button>

          <button
            onClick={toggleFullscreen}
            className="p-2 rounded-full bg-[#0F0F16] text-gray-300 hover:text-white"
          >
            {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {/* Main content area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Video grid */}
        <div className={`flex-1 p-4 ${showChat || showParticipants ? 'pr-0' : ''}`}>
          <div
            className={`h-full grid gap-3 ${
              layout === 'spotlight' && allParticipants.length > 1
                ? 'grid-cols-4 grid-rows-3'
                : `grid-cols-${gridCols}`
            }`}
            style={{
              gridTemplateColumns: layout === 'spotlight' && allParticipants.length > 1
                ? 'repeat(4, 1fr)'
                : `repeat(${gridCols}, 1fr)`
            }}
          >
            {allParticipants.map((participant, index) => (
              <ParticipantTile
                key={participant?.identity || index}
                participant={participant}
                isLocal={participant?.isLocal}
                isLarge={layout === 'spotlight' && index === 0}
              />
            ))}
          </div>
        </div>

        {/* Sidebar - Participants list */}
        <AnimatePresence>
          {showParticipants && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 280, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              className="bg-[#08080C] border-l border-white/5 overflow-hidden"
            >
              <div className="p-4 h-full flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-white">Participants ({allParticipants.length})</h3>
                  <button onClick={() => setShowParticipants(false)} className="text-gray-400 hover:text-white">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto space-y-2">
                  {allParticipants.map((p, i) => (
                    <div key={p?.identity || i} className="flex items-center gap-3 p-2 rounded-lg bg-[#0F0F16]">
                      <div className="w-8 h-8 rounded-full bg-[#00F0FF]/20 flex items-center justify-center">
                        <span className="text-sm font-medium text-[#00F0FF]">
                          {p?.name?.[0]?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <span className="text-sm text-white flex-1 truncate">{p?.name}</span>
                      {p?.isLocal && <Crown className="w-4 h-4 text-[#FFE800]" />}
                    </div>
                  ))}
                </div>
                <button
                  onClick={copyRoomLink}
                  className="mt-4 w-full py-2 rounded-full bg-[#00F0FF] text-black font-semibold flex items-center justify-center gap-2"
                >
                  <UserPlus className="w-4 h-4" />
                  Invite Others
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Sidebar - Chat */}
        <AnimatePresence>
          {showChat && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 320, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              className="bg-[#08080C] border-l border-white/5 overflow-hidden"
            >
              <div className="p-4 h-full flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-white">Chat</h3>
                  <button onClick={() => setShowChat(false)} className="text-gray-400 hover:text-white">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="flex-1 overflow-y-auto space-y-3 mb-4">
                  {messages.length === 0 ? (
                    <p className="text-gray-500 text-center text-sm py-8">No messages yet</p>
                  ) : (
                    messages.map((msg, i) => (
                      <div key={i} className={`${msg.isLocal ? 'text-right' : ''}`}>
                        <div className={`inline-block max-w-[85%] p-3 rounded-2xl ${
                          msg.isLocal ? 'bg-[#00F0FF] text-black' : 'bg-[#0F0F16] text-white'
                        }`}>
                          {!msg.isLocal && (
                            <p className="text-xs text-[#00F0FF] font-medium mb-1">{msg.sender}</p>
                          )}
                          <p className="text-sm">{msg.text}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    placeholder="Type a message..."
                    className="flex-1 px-4 py-2 rounded-full bg-[#0F0F16] border border-white/10 text-white text-sm outline-none focus:border-[#00F0FF]"
                  />
                  <button
                    onClick={sendMessage}
                    className="p-2 rounded-full bg-[#00F0FF] text-black"
                  >
                    <Share2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Control bar */}
      <div className="flex items-center justify-center gap-3 py-4 bg-[#08080C] border-t border-white/5">
        <button
          onClick={toggleAudio}
          className={`p-4 rounded-full transition-all ${
            audioEnabled
              ? 'bg-[#0F0F16] text-white hover:bg-[#1a1a24]'
              : 'bg-[#FF2A2A] text-white'
          }`}
          data-testid="toggle-audio-btn"
        >
          {audioEnabled ? <Mic className="w-6 h-6" /> : <MicOff className="w-6 h-6" />}
        </button>

        <button
          onClick={toggleVideo}
          className={`p-4 rounded-full transition-all ${
            videoEnabled
              ? 'bg-[#0F0F16] text-white hover:bg-[#1a1a24]'
              : 'bg-[#FF2A2A] text-white'
          }`}
          data-testid="toggle-video-btn"
        >
          {videoEnabled ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
        </button>

        <button
          onClick={toggleScreenShare}
          className={`p-4 rounded-full transition-all ${
            screenSharing
              ? 'bg-[#00FF66] text-black'
              : 'bg-[#0F0F16] text-white hover:bg-[#1a1a24]'
          }`}
          data-testid="toggle-screenshare-btn"
        >
          {screenSharing ? <MonitorOff className="w-6 h-6" /> : <Monitor className="w-6 h-6" />}
        </button>

        <button
          onClick={() => setShowChat(!showChat)}
          className={`p-4 rounded-full transition-all ${
            showChat
              ? 'bg-[#00F0FF] text-black'
              : 'bg-[#0F0F16] text-white hover:bg-[#1a1a24]'
          }`}
          data-testid="toggle-chat-btn"
        >
          <MessageSquare className="w-6 h-6" />
        </button>

        <button
          onClick={() => setShowParticipants(!showParticipants)}
          className={`p-4 rounded-full transition-all ${
            showParticipants
              ? 'bg-[#00F0FF] text-black'
              : 'bg-[#0F0F16] text-white hover:bg-[#1a1a24]'
          }`}
          data-testid="toggle-participants-btn"
        >
          <Users className="w-6 h-6" />
        </button>

        <div className="w-px h-8 bg-white/10 mx-2" />

        <button
          onClick={leaveCall}
          className="px-6 py-4 rounded-full bg-[#FF2A2A] text-white font-semibold hover:shadow-[0_0_20px_rgba(255,42,42,0.4)] transition-all"
          data-testid="leave-call-btn"
        >
          <Phone className="w-6 h-6 rotate-[135deg]" />
        </button>
      </div>
    </div>
  );
};

export default GroupVideoCall;
